import { useQuery, useSubscription } from '@apollo/client';
import { css } from '@emotion/core';
import { Button, Theme, useTheme } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { groupBy, orderBy, times, uniq } from 'lodash';
import moment from 'moment';
import React from 'react';

import { InterviewKind } from 'shared/models/interview';
import { hasRole } from 'shared/models/user';

import { JobTitle } from '../containers/job-title';
import {
    AI_INTERVIEW_REQUESTS,
    AIInterviewRequest,
    AIInterviewRequestsVariables
} from '../graphql/queries/ai-interview-requests';
import { Interviews, INTERVIEWS, INTERVIEWS_LATEST_MODIFICATION } from '../graphql/queries/interviews';
import { useReduxState } from '../hooks/use-redux';
import { useSession } from '../hooks/use-session';
import { PersonAIInterviews } from './person-ai-interviews';
import { PersonInterview } from './person-interview';
import { PersonPulseAssessment } from './person-pulse-assessment';

const styles = (theme: Theme) => css`
    .interviews-nav {
        display: flex;
        justify-content: flex-end;
        font-size: 12px;
        text-transform: uppercase;
        white-space: nowrap;
        margin: 4px 0 25px;

        .select-tab {
            padding: 2px 20px;
            margin: 0;
            font-weight: 500;
            cursor: pointer;
            color: ${theme.palette.text.secondary};
            border-right: thin solid ${theme.palette.divider};
            display: inline-flex;

            &.active {
                color: ${theme.palette.text.primary};
                font-weight: 600;
            }

            &:last-child {
                border-right: none;
                padding-right: 0;
            }

            &:first-child {
                padding-left: 0;
            }
        }
    }

    .interviews {
        margin: -10px -10px;
        display: flex;
        flex-wrap: wrap;
    }

    .job-interviews {
        margin-bottom: 40px;
    }

    .ai-interview-skeleton {
        width: 360px;
        max-width: 100%;
        height: 36px;
        border-radius: ${theme.shape.borderRadius}px;
    }

    .interviews-job-title {
        margin-bottom: 12px;
        font-weight: 500;
        display: inline-flex;
        align-items: center;
    }

    .interview {
        width: 160px;
        height: 200px;
        margin: 10px 10px;
        border-radius: ${theme.shape.borderRadius}px;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        &.add {
            .MuiButton-root {
                height: 100%;

                .MuiButton-label {
                    height: 100%;

                    .MuiSvgIcon-root {
                        font-size: 96px;
                        color: white;
                    }
                }
            }
        }
    }
`;

const skeletonsCount = 3;
const rocketInterviewKinds: InterviewKind[] = ['Rocket Screen'];
const clientInterviewKinds: InterviewKind[] = ['Client First Round', 'Client Middle Round', 'Client Final Round'];
const allInterviewKinds: InterviewKind[] = [...rocketInterviewKinds, ...clientInterviewKinds];

export const PersonInterviews: React.FC<{ personId: string; jobId?: string }> = ({ personId, jobId }) => {
    const theme = useTheme();
    const [allJobs, setAllJobs] = React.useState(false);
    const [adding, setAdding] = React.useState(false);
    const { data, refetch } = useQuery<Interviews, { personId: string; jobId: string }>(INTERVIEWS, {
        variables: { personId, jobId: jobId ?? '' }
    });
    const {
        data: aiData,
        loading: aiLoading,
        refetch: refetchAI
    } = useQuery<{ interviewRequests: AIInterviewRequest[] }, AIInterviewRequestsVariables>(AI_INTERVIEW_REQUESTS, {
        fetchPolicy: 'cache-and-network',
        variables: { where: { personId: { _eq: personId } } }
    });
    const { data: interviewLatestModifiedData } = useSubscription<
        { interviews: Array<{ modifiedAt: number }> },
        { personId: string }
    >(INTERVIEWS_LATEST_MODIFICATION, { variables: { personId } });
    const { user, userPermissions } = useSession();
    const jobs = useReduxState((state) => state.jobs);
    const clients = useReduxState((state) => state.clients.list);

    React.useEffect(() => {
        if (interviewLatestModifiedData?.interviews?.[0]?.modifiedAt) {
            refetch();
        }
    }, [interviewLatestModifiedData?.interviews?.[0]?.modifiedAt]);

    const handleJobTabClick = (all: boolean) => () => setAllJobs(all);
    const handleAddInterview = () => setAdding(true);
    const handleCancelAdd = () => setAdding(false);
    const handleUpdated = async () => {
        await refetch();
        setAdding(false);
    };

    const jobTabs = !!jobId ? (
        <div className="job-select">
            <div className={`select-tab ${allJobs ? 'active' : ''}`} onClick={handleJobTabClick(true)}>
                All Jobs
            </div>
            <div className={`select-tab ${!allJobs ? 'active' : ''}`} onClick={handleJobTabClick(false)}>
                Current Job
            </div>
        </div>
    ) : null;

    let content;
    if (!data?.interviews) {
        const loaders = times(skeletonsCount).map((i) => (
            <Skeleton className="interview skeleton" variant="rect" key={i} />
        ));
        content = <div className="interviews">{loaders}</div>;
    } else {
        const groupedInterviews = groupBy(data.interviews, (interview) => interview.job.id);
        const groupedAI = groupBy(aiData?.interviewRequests ?? [], (request) => request.jobId);
        const jobIds = orderBy(
            uniq([...Object.keys(groupedInterviews), ...Object.keys(groupedAI), jobId].filter(Boolean)),
            [
                (id) => id === jobId,
                (id) => clients.get(jobs.get(id)?.clientId)?.name ?? '',
                (id) => jobs.get(id)?.title ?? id
            ],
            ['desc', 'asc', 'asc']
        );

        const filteredJobIds = allJobs || !jobId ? jobIds : [jobId];

        content = filteredJobIds.map((id) => {
            const interviews = orderBy(
                groupedInterviews[id] ?? [],
                [(i) => allInterviewKinds.indexOf(i.kind), (i) => i.startTime],
                ['asc', 'asc']
            );
            const title =
                allJobs || !jobId ? (
                    <div className="interviews-job-title">
                        <JobTitle id={id} />
                    </div>
                ) : null;
            const list = interviews.map((interview) => {
                const validKinds: InterviewKind[] =
                    interview.kind === 'Rocket Screen' ? rocketInterviewKinds : clientInterviewKinds;
                return (
                    <PersonInterview
                        data={interview}
                        key={interview.id}
                        onSave={handleUpdated}
                        onDelete={handleUpdated}
                        validKinds={validKinds}
                    />
                );
            });
            const canAdd =
                jobId &&
                id === jobId &&
                (hasRole(userPermissions, 'settings_editor') ||
                    data?.candidate?.assignee === user.id ||
                    data?.candidate?.accountManagerId === user.id);
            const addInterviewButton =
                canAdd && !adding ? (
                    <div className="interview add">
                        <Button variant="contained" onClick={handleAddInterview}>
                            <Add />
                        </Button>
                    </div>
                ) : null;
            let newInterviewComponent;
            if (adding && id === jobId) {
                const latestInterviewKindIndex =
                    interviews.length === 0 ? 0 : Math.max(...interviews.map((i) => allInterviewKinds.indexOf(i.kind)));
                const rocketScreenExists = interviews.findIndex((i) => i.kind === 'Rocket Screen') !== -1;
                const newInterviewValidKinds = allInterviewKinds
                    .slice(latestInterviewKindIndex)
                    .filter((k) => !rocketScreenExists || k !== 'Rocket Screen');
                const durationMinutes = 30;
                const startTime = moment().add(1, 'hour').startOf('hour').valueOf();
                const endTime = moment(startTime).add(durationMinutes, 'minutes').valueOf();
                const kind = newInterviewValidKinds[0];
                const newInterviewData = {
                    endTime,
                    jobId,
                    kind,
                    personId,
                    startTime
                };
                newInterviewComponent = (
                    <PersonInterview
                        data={newInterviewData}
                        onSave={handleUpdated}
                        onDelete={handleCancelAdd}
                        validKinds={newInterviewValidKinds}
                    />
                );
            }
            const aiInterviews =
                aiData && (groupedAI[id] || id === jobId) ? (
                    <PersonAIInterviews
                        personId={personId}
                        jobId={id === jobId ? jobId : undefined}
                        requests={groupedAI[id] ?? []}
                        onCreated={refetchAI}
                    />
                ) : null;
            return (
                <div className="job-interviews" key={id}>
                    {title}
                    <div className="interviews">
                        {list}
                        {addInterviewButton}
                        {newInterviewComponent}
                    </div>
                    {aiInterviews}
                </div>
            );
        });
    }

    const aiLoader =
        aiLoading && !aiData ? (
            <div aria-label="AI interviews" aria-busy={true}>
                <Skeleton className="ai-interview-skeleton" variant="rect" />
            </div>
        ) : null;
    const pulseAssessment = jobId ? <PersonPulseAssessment personId={personId} jobId={jobId} /> : null;
    return (
        <div css={styles(theme)}>
            <div className="interviews-nav">{jobTabs}</div>
            {content}
            {aiLoader}
            {pulseAssessment}
        </div>
    );
};
